// REST client for the Cloudflare Worker. Components import `api` from
// ../api and call typed helpers like `api.getVendor(id)`. Each helper
// returns the parsed JSON body directly; errors are thrown.

import config from "./config";
import { getAuthToken } from "./auth";

async function request(method, path, { body, query } = {}) {
  const url = new URL(config.apiBaseUrl + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== "") url.searchParams.set(k, v);
    }
  }
  const headers = { "content-type": "application/json" };
  const token = getAuthToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await response.json();
  } catch {}
  if (!response.ok) {
    const error = new Error((data && data.error) || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

export const api = {
  // Vendors
  listVendors: (params) => request("GET", "/api/vendors", { query: params }),
  getVendor: (id) => request("GET", `/api/vendors/${encodeURIComponent(id)}`),
  listVendorItems: (id, params) =>
    request("GET", `/api/vendors/${encodeURIComponent(id)}/items`, { query: params }),
  listVendorReviews: (id, params) =>
    request("GET", `/api/vendors/${encodeURIComponent(id)}/reviews`, { query: params }),
  createVendor: (body) => request("POST", "/api/vendors", { body }),
  updateVendor: (id, body) =>
    request("PATCH", `/api/vendors/${encodeURIComponent(id)}`, { body }),
  deleteVendor: (id) => request("DELETE", `/api/vendors/${encodeURIComponent(id)}`),

  // Reviewable items
  listItems: (params) => request("GET", "/api/items", { query: params }),
  getItem: (id) => request("GET", `/api/items/${encodeURIComponent(id)}`),
  listItemReviews: (id, params) =>
    request("GET", `/api/items/${encodeURIComponent(id)}/reviews`, { query: params }),
  createItem: (body) => request("POST", "/api/items", { body }),
  updateItem: (id, body) =>
    request("PATCH", `/api/items/${encodeURIComponent(id)}`, { body }),
  deleteItem: (id) => request("DELETE", `/api/items/${encodeURIComponent(id)}`),

  // Reviews
  listReviews: (params) => request("GET", "/api/reviews", { query: params }),
  getReview: (id) => request("GET", `/api/reviews/${encodeURIComponent(id)}`),
  createReview: (body) => request("POST", "/api/reviews", { body }),
  updateReview: (id, body) =>
    request("PATCH", `/api/reviews/${encodeURIComponent(id)}`, { body }),
  deleteReview: (id) => request("DELETE", `/api/reviews/${encodeURIComponent(id)}`),
  listMyDrafts: () => request("GET", "/api/reviews/drafts"),

  // Users
  getMe: () => request("GET", "/api/users/me"),
  updateMe: (body) => request("PATCH", "/api/users/me", { body }),
  getUser: (id) => request("GET", `/api/users/${encodeURIComponent(id)}`),
  getUserByUsername: (username) =>
    request("GET", `/api/users/by-username/${encodeURIComponent(username)}`),
  listUserReviews: (id, params) =>
    request("GET", `/api/users/${encodeURIComponent(id)}/reviews`, { query: params }),

  // Uploads
  uploadSignature: (contentType) =>
    request("POST", "/api/uploads/signature", { body: { contentType } }),
};

export default api;
